/**
 * Sanitize untrusted HTML (EMR attachments) for inline preview.
 *
 * Attached documents are EMR-generated but still untrusted input. The preview
 * renders them via `iframe sandbox=""` (no scripts, no same-origin, no forms),
 * and this strip removes the parts the sandbox cannot guarantee away from the
 * *content itself*: script/link/meta/base/object/embed/iframe elements, inline
 * event handlers, and javascript:/data:text/html URLs. CSS is kept so the form
 * stays legible — with scripts disabled it cannot do anything.
 *
 * @param html - Raw HTML document text
 * @returns Sanitized HTML safe for a sandboxed iframe srcDoc
 */
export const sanitizeHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc
    .querySelectorAll("script, link, meta, base, object, embed, iframe")
    .forEach((el) => el.remove());
  doc.querySelectorAll("*").forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      const isEvent = name.startsWith("on");
      const isUrlAttr = ["href", "src", "xlink:href", "action", "formaction"].includes(name);
      const isUnsafeUrl = isUrlAttr && (value.startsWith("javascript:") || value.startsWith("data:text/html"));
      if (isEvent || isUnsafeUrl) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.documentElement.outerHTML;
};